import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

type Lobby = {
  id: string;
  name: string;
  description?: string;
  max_players?: number;
};

const LobbiesPage: React.FC = () => {
  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [selectedQr, setSelectedQr] = useState<string | null>(null);

  useEffect(() => {
    fetchLobbies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLobbies = async () => {
    setLoading(true);
    try {
      const userRes = await supabase.auth.getUser();
      const user = userRes?.data?.user;
      if (!user) {
        setLobbies([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('lobbies')
        .select('*')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching lobbies', error);
        setLobbies([]);
      } else if (data) {
        setLobbies(data.map((d: any) => ({ id: String(d.id), name: d.name, description: d.description, max_players: d.max_players })));
      }
    } catch (err) {
      console.error(err);
      setLobbies([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto glass card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">My Lobbies</h2>
        <button onClick={() => navigate('/create')} className="px-3 py-2 rounded-full bg-accent text-white">Create Lobby</button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : lobbies.length === 0 ? (
        <p className="text-gray-500">You haven't created any lobbies yet.</p>
      ) : (
        <ul className="space-y-3">
          {lobbies.map((l) => (
            <li key={l.id} className="p-3 bg-white/80 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-3 sm:mb-0">
                <div className="font-medium">{l.name}</div>
                <div className="text-sm text-gray-500">{l.description}</div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 space-y-2 sm:space-y-0">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setSelectedQr(selectedQr === l.id ? null : l.id)}
                    className="px-3 py-1 rounded-full bg-gray-100"
                  >
                    Join URL
                  </button>
                  <button onClick={() => navigate(`/lobby/${l.id}/matchmaker`)} className="px-3 py-1 rounded-full bg-accent text-white">Open Matchmaker</button>
                </div>
              </div>

              {selectedQr === l.id && (
                <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white/90 p-3 rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className="w-32 h-32 flex items-center justify-center bg-white rounded-md p-1">
                      <QRCodeSVG value={`${window.location.origin}/join/${l.id}`} size={120} />
                    </div>
                    <div className="flex flex-col">
                      <div className="text-sm font-medium">Share link</div>
                      <div className="text-xs text-gray-600 break-all max-w-xs">{`${window.location.origin}/join/${l.id}`}</div>
                      <div className="mt-2 flex space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(`${window.location.origin}/join/${l.id}`);
                              // small confirmation — could be improved to toast
                              alert('Link copied to clipboard');
                            } catch (err) {
                              console.error('Copy failed', err);
                            }
                          }}
                          className="px-3 py-1 rounded-full bg-gray-100 text-sm"
                        >
                          Copy link
                        </button>
                        <button
                          onClick={() => setSelectedQr(null)}
                          className="px-3 py-1 rounded-full bg-red-50 text-red-700 text-sm"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LobbiesPage;
